/**
 * 🚨 Crisis Report Type Definitions
 * 
 * Defines the complete 58-field structure for crisis reports submitted from mobile
 * and managed through the web triage system.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Crisis Reports Triage Implementation
 */

/**
 * Crisis report types supported by the mobile app
 * @typedef {'fire' | 'active_threat' | 'medical' | 'flood' | 'bomb_threat' | 'kidnap' | 'major_vehicle'} CrisisReportType
 */

/**
 * Severity levels for crisis reports
 * @typedef {'Low' | 'Med' | 'High' | 'Critical'} CrisisReportSeverity
 */

/**
 * Crisis report status progression through triage workflow
 * @typedef {'submitted' | 'acknowledged' | 'promoted' | 'declined'} CrisisReportStatus
 */

/**
 * Location data from mobile device
 * @typedef {Object} CrisisReportLocation
 * @property {number} lat - Latitude coordinate
 * @property {number} lng - Longitude coordinate  
 * @property {number} accuracy - GPS accuracy in meters
 */

/**
 * Complete Crisis Report structure (58 fields)
 * Matches the enriched document created by the Cloud Function
 * 
 * @typedef {Object} CrisisReport
 * 
 * // Identity & Organization (4 fields)
 * @property {string} id - Firestore document ID
 * @property {string} orgId - Organization ID (tenant isolation)
 * @property {string} siteId - Site ID where report originated
 * @property {string|null} siteName - Site name (nullable, fallback to siteId)
 * 
 * // Reporter Information (4 fields)
 * @property {string} reporterUid - Firebase user UID of reporter
 * @property {string} reporterName - Display name of reporter
 * @property {string} reporterEmail - Email address of reporter
 * @property {string|null} clientReportId - Optional client-side report ID
 * 
 * // Event Details (6 fields)
 * @property {CrisisReportType} type - Type of crisis reported
 * @property {CrisisReportSeverity|null} severityGuess - Reporter's severity assessment
 * @property {string} summary - Brief crisis summary
 * @property {string} details - Optional detailed description
 * @property {string} occurredAt - ISO timestamp when crisis occurred
 * @property {CrisisReportLocation} location - GPS coordinates and accuracy
 * 
 * // Media Attachments (4 fields)
 * @property {number} pendingMediaCount - Count of media uploads in progress
 * @property {number} attachmentsCount - Count of completed attachments
 * @property {Array} attachments - Array of attachment metadata
 * @property {boolean} attachmentsOverflowed - Whether attachment limit exceeded
 * 
 * // Workflow Status (2 fields)
 * @property {CrisisReportStatus} status - Current triage status
 * @property {string|null} statusReason - Reason for current status
 * 
 * // Acknowledge Action (2 fields)
 * @property {string|null} acknowledgedBy - UID of user who acknowledged
 * @property {import('firebase/firestore').Timestamp|null} acknowledgedAt - When acknowledged
 * 
 * // Promote Action (3 fields)
 * @property {string|null} promotedByUid - UID of user who promoted
 * @property {import('firebase/firestore').Timestamp|null} promotedAt - When promoted
 * @property {string|null} reviewNote - Review note for promotion
 * 
 * // Decline Action (3 fields)
 * @property {string|null} declinedByUid - UID of user who declined
 * @property {import('firebase/firestore').Timestamp|null} declinedAt - When declined
 * @property {string|null} reviewNote - Review note for decline (shares field with promote)
 * 
 * // Enhanced Location Data (5 fields)
 * @property {string|null} locationAddress - Geocoded address from coordinates
 * @property {string|null} locationCity - City from geocoding
 * @property {string|null} locationState - State from geocoding
 * @property {string|null} locationCountry - Country from geocoding
 * @property {string|null} locationPostcode - Postcode from geocoding
 * 
 * // Device & Client Information (4 fields)
 * @property {string|null} deviceType - Type of device used for submission
 * @property {string|null} appVersion - Mobile app version
 * @property {string|null} platform - Platform (iOS/Android)
 * @property {string|null} userAgent - User agent string
 * 
 * // Operational Intelligence (6 fields)
 * @property {boolean} rateLimited - Whether submission was rate limited
 * @property {import('firebase/firestore').Timestamp|null} rateLimitedAt - When rate limiting applied
 * @property {boolean} suspectedAbuse - Whether flagged as potential abuse
 * @property {string} dedupeGroupId - Hash for deduplication
 * @property {string} sensitivity - Sensitivity level ('normal' default)
 * @property {string} shortRef - Unique 6-character reference
 * 
 * // Processing Metadata (5 fields)
 * @property {number} processingTimeMs - Time taken to process submission
 * @property {string} functionVersion - Cloud Function version that processed
 * @property {boolean} enrichmentComplete - Whether all enrichment completed
 * @property {Array} enrichmentErrors - Any errors during enrichment
 * @property {string} submissionSource - Source of submission (mobile/web/api)
 * 
 * // Notification & Communication (4 fields)
 * @property {boolean} notificationsSent - Whether notifications were sent
 * @property {Array} notificationRecipients - List of notification recipients
 * @property {import('firebase/firestore').Timestamp|null} notificationsSentAt - When notifications sent
 * @property {string|null} escalationLevel - Current escalation level
 * 
 * // Audit Trail (2 fields)
 * @property {string} createdByUid - UID of user who created (system for mobile)
 * @property {string} createdByEmail - Email of user who created
 * 
 * // Timestamps (4 fields)
 * @property {string} clientCreatedAt - ISO timestamp from mobile client
 * @property {import('firebase/firestore').Timestamp} receivedAt - Server timestamp when received
 * @property {import('firebase/firestore').Timestamp} createdAt - Server timestamp when created
 * @property {import('firebase/firestore').Timestamp} updatedAt - Server timestamp when last updated
 */

/**
 * Crisis report filter options for queries
 * @typedef {Object} CrisisReportFilters
 * @property {CrisisReportStatus} [status] - Filter by status
 * @property {CrisisReportType} [type] - Filter by crisis type
 * @property {string} [dateFrom] - Filter from date (ISO string)
 * @property {string} [dateTo] - Filter to date (ISO string)
 */

/**
 * Status update patch for crisis reports
 * @typedef {Object} CrisisReportStatusUpdate
 * @property {CrisisReportStatus} status - New status
 * @property {string} [acknowledgedBy] - UID for acknowledge action
 * @property {import('firebase/firestore').Timestamp} [acknowledgedAt] - Timestamp for acknowledge
 * @property {string} [promotedByUid] - UID for promote action
 * @property {import('firebase/firestore').Timestamp} [promotedAt] - Timestamp for promote
 * @property {string} [declinedByUid] - UID for decline action
 * @property {import('firebase/firestore').Timestamp} [declinedAt] - Timestamp for decline
 * @property {string} [reviewNote] - Review note for promote/decline actions
 * @property {import('firebase/firestore').Timestamp} updatedAt - Server timestamp
 */

/**
 * Crisis report service method signatures
 * @typedef {Object} CrisisReportServiceMethods
 * @property {function(string, CrisisReportFilters, function): function} subscribeToReports - Real-time listener
 * @property {function(string): function} getSubmittedCount - Real-time count listener
 * @property {function(string, CrisisReportStatusUpdate): Promise<void>} updateReportStatus - Generic status update
 * @property {function(string, string): Promise<void>} acknowledgeReport - Acknowledge report
 * @property {function(string, string, string): Promise<void>} promoteReport - Promote report
 * @property {function(string, string, string): Promise<void>} declineReport - Decline report
 */

// Export types for JSDoc usage
export const CRISIS_REPORT_TYPES = {
  FIRE: 'fire',
  ACTIVE_THREAT: 'active_threat', 
  MEDICAL: 'medical',
  FLOOD: 'flood',
  BOMB_THREAT: 'bomb_threat',
  KIDNAP: 'kidnap',
  MAJOR_VEHICLE: 'major_vehicle'
};

export const CRISIS_REPORT_SEVERITIES = {
  LOW: 'Low',
  MED: 'Med', 
  HIGH: 'High',
  CRITICAL: 'Critical'
};

export const CRISIS_REPORT_STATUSES = {
  SUBMITTED: 'submitted',
  ACKNOWLEDGED: 'acknowledged',
  PROMOTED: 'promoted', 
  DECLINED: 'declined'
};

export const CRISIS_REPORT_SENSITIVITY_LEVELS = {
  NORMAL: 'normal'
};
